import dayjs from 'dayjs';
import React, { useEffect, useRef, useState } from 'react';
import { DayPickerProps } from 'react-day-picker';
import { List, ListImperativeAPI } from 'react-window';

import { DatePicker, TMinimumStay } from '@/components/common/DatePicker';

type TVirtualizedDatePickerProps = {
  dateFrom?: Date;
  dateTo?: Date;
  onChange?: (args: { from: Date | undefined; to: Date | undefined }) => void;
  disabledDays?: DayPickerProps['disabled'];
  disableBefore?: Date;
  lockStartDate?: boolean;
  fromMonth?: Date;
  minimumStayRequirement?: TMinimumStay;
  allowSameDay?: boolean;

  // Virtualization props
  totalMonths?: number;
  containerHeight?: number;
  activeMonth?: Date;
  onMonthChange?: (month: Date) => void;
};

const WEEK_HEIGHT = 48; // Height of each week row
const HEADER_HEIGHT = 80; // Height for month header and padding
const CONTAINER_HEIGHT = 600; // Visible container height

// Function to calculate the height of a specific month
const getMonthHeight = (monthDate: Date): number => {
  const firstDay = dayjs(monthDate).startOf('month');
  const lastDay = dayjs(monthDate).endOf('month');

  // Calculate number of weeks needed for this month
  // We need to account for the first week (which might be partial)
  // and ensure we show complete weeks
  const startWeek = firstDay.startOf('week');
  const endWeek = lastDay.endOf('week');
  const weeksCount = endWeek.diff(startWeek, 'week') + 1;

  // Return calculated height: header + (weeks * week height) + padding
  return HEADER_HEIGHT + weeksCount * WEEK_HEIGHT;
};

// Props that will be passed to each row
interface MonthRowProps {
  dateFrom?: Date;
  dateTo?: Date;
  onChange?: (args: { from: Date | undefined; to: Date | undefined }) => void;
  disabledDays?: DayPickerProps['disabled'];
  disableBefore?: Date;
  lockStartDate?: boolean;
  fromMonth: Date;
  minimumStayRequirement?: TMinimumStay;
  allowSameDay?: boolean;
  onMonthChange?: (month: Date) => void;
  totalMonths: number;

  // Shared hover state
  hoveredDay?: Date;
  onDayHover: (day: Date | undefined) => void;

  index: never;
  style: never;
}

// Row component that will be rendered for each month
const MonthRow: React.FC<MonthRowProps> = ({
  index,
  dateFrom,
  dateTo,
  onChange,
  disabledDays,
  disableBefore,
  lockStartDate,
  fromMonth,
  minimumStayRequirement,
  allowSameDay,
  onMonthChange,
  hoveredDay,
  onDayHover,
  style,
}) => {
  const monthDate = dayjs(fromMonth).add(index, 'month').startOf('month').toDate();

  const handleMonthClick = () => {
    onMonthChange?.(monthDate);
  };

  return (
    <div
      onClick={handleMonthClick}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleMonthClick();
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={`Calendar for ${dayjs(monthDate).format('MMMM YYYY')}`}
      style={style}
      className="flex-shrink-0">
      <div className="px-3 py-2">
        <DatePicker
          numberOfMonths={1}
          range
          isStacked
          month={monthDate}
          dateFrom={dateFrom}
          dateTo={dateTo}
          onChange={onChange}
          disabledDays={disabledDays}
          disableBefore={disableBefore}
          lockStartDate={lockStartDate}
          minimumStayRequirement={minimumStayRequirement}
          allowSameDay={allowSameDay}
          showOutsideDays={false}
          fixedWeeks
          disableNavigation
          size="small"
          hoveredDay={hoveredDay}
          onDayHover={onDayHover}
        />
      </div>
    </div>
  );
};

export const VirtualizedDatePicker = ({
  dateFrom,
  dateTo,
  onChange,
  disabledDays,
  disableBefore,
  lockStartDate,
  fromMonth = dayjs().toDate(),
  minimumStayRequirement,
  allowSameDay,
  totalMonths = 24,
  containerHeight = CONTAINER_HEIGHT,
  onMonthChange,
}: TVirtualizedDatePickerProps) => {
  const listRef = useRef<ListImperativeAPI>(null);

  // Shared hover state for all months
  const [hoveredDay, setHoveredDay] = useState<Date | undefined>();

  // Function to calculate row height based on month index
  const getRowHeight = (index: number): number => {
    const monthDate = dayjs(fromMonth).add(index, 'month').startOf('month').toDate();
    return getMonthHeight(monthDate);
  };

  // Row data that will be passed to each MonthRow
  const rowData: MonthRowProps = {
    dateFrom,
    dateTo,
    onChange,
    disabledDays,
    disableBefore,
    lockStartDate,
    fromMonth,
    minimumStayRequirement,
    allowSameDay,
    onMonthChange,
    totalMonths,
    hoveredDay,
    onDayHover: setHoveredDay,
    index: undefined as never,
    style: undefined as never,
  };

  useEffect(() => {
    const index = dayjs(dateFrom).diff(dayjs(fromMonth), 'month');

    if (!listRef?.current) return;

    listRef?.current?.scrollToRow({
      index,
      align: 'start',
      behavior: 'instant',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listRef.current]);

  return (
    <div style={{ height: containerHeight, width: '100%' }}>
      <List
        listRef={listRef}
        defaultHeight={containerHeight}
        rowCount={totalMonths}
        rowHeight={getRowHeight}
        rowComponent={MonthRow}
        rowProps={rowData}
        overscanCount={2}
        className="scrollbar-none"
      />
    </div>
  );
};
